import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon, ButtonLink, Select, Toggle } from '@resideo/blueprint-oneportal';
import styles from './Profile.module.css';
import { useAuth } from '@/contexts/AuthContext';
import { chevronLeftIcon, externalLinkIcon } from '@/assets/icons';

type ProfileTab = 'profile' | 'preferences' | 'notifications';
type TemperatureUnit = 'fahrenheit' | 'celsius';
type ClockFormat = '12h' | '24h';

const TIMEZONE_STORAGE_KEY = 'oneportal_pref_timezone';
const CLOCK_STORAGE_KEY = 'oneportal_pref_clock_format';
const LANGUAGE_STORAGE_KEY = 'oneportal_pref_language';
const TEMPERATURE_STORAGE_KEY = 'oneportal_pref_temperature_unit';

function readStoredPreference<T extends string>(key: string, fallback: T): T {
  const stored = localStorage.getItem(key);
  return (stored as T) || fallback;
}

interface NotificationRow {
  id: string;
  label: string;
  email: boolean;
  push: boolean;
  inApp: boolean;
}

const CAMERA_NOTIFICATIONS: NotificationRow[] = [
  { id: 'animal_detected', label: 'Animal Detected', email: false, push: false, inApp: true },
  { id: 'motion_detected', label: 'Motion Detected', email: false, push: false, inApp: true },
  { id: 'package_detected', label: 'Package Detected', email: false, push: false, inApp: true },
  { id: 'person_detected', label: 'Person Detected', email: false, push: false, inApp: true },
  { id: 'vehicle_detected', label: 'Vehicle Detected', email: false, push: false, inApp: true },
];

const HVAC_NOTIFICATIONS: NotificationRow[] = [
  { id: 'high_humidity', label: 'High Humidity', email: false, push: false, inApp: true },
  { id: 'low_humidity', label: 'Low Humidity', email: false, push: false, inApp: true },
  { id: 'high_temperature', label: 'High Temperature', email: false, push: false, inApp: true },
  { id: 'low_temperature', label: 'Low Temperature', email: false, push: true, inApp: true },
  { id: 'connection_status', label: 'Connection Status', email: false, push: true, inApp: true },
  { id: 'filter_change', label: 'Filter Change', email: true, push: false, inApp: false },
  { id: 'sensor_fault', label: 'Sensor Fault', email: false, push: false, inApp: true },
  { id: 'low_battery', label: 'Low Battery', email: false, push: false, inApp: true },
  { id: 'sensor_offline', label: 'Sensor Offline', email: false, push: false, inApp: true },
];

function NotificationTable({
  title,
  devicesLabel,
  rows,
  onToggle,
}: {
  title: string;
  devicesLabel: string;
  rows: NotificationRow[];
  onToggle: (id: string, field: 'email' | 'push' | 'inApp') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.notificationSection}>
      <div className={styles.card}>
        <h4 className={styles.cardTitle}>{title}</h4>
        <table className={styles.notificationTable}>
        <thead>
          <tr>
            <th>{t('profile.table.notificationEvent', 'Notification Event')}</th>
            <th>{t('profile.table.emailMe', 'Email Me')}</th>
            <th>{t('profile.table.push', 'Push')}</th>
            <th>{t('profile.table.inApp', 'In App')}</th>
            <th>{t('profile.table.when', 'When')}</th>
            <th>{t('profile.table.devices', 'Devices')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className={styles.eventCell}>{t(`profile.notificationEvents.${row.id}`, row.label)}</td>
              <td>
                <Toggle name={`${row.id}-email`} checked={row.email} onChange={() => onToggle(row.id, 'email')} />
              </td>
              <td>
                <Toggle name={`${row.id}-push`} checked={row.push} onChange={() => onToggle(row.id, 'push')} />
              </td>
              <td>
                <Toggle name={`${row.id}-inApp`} checked={row.inApp} onChange={() => onToggle(row.id, 'inApp')} />
              </td>
              <td>
                <Select
                  name={`${row.id}-when`}
                  defaultValue="always"
                  items={[
                    { value: 'always', label: t('profile.when.always', 'Always') },
                    { value: 'never', label: t('profile.when.never', 'Never') },
                  ]}
                />
              </td>
              <td className={styles.devicesCell}>{devicesLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, selectedContext, activeRole } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [timeZone, setTimeZone] = useState<string>(() => readStoredPreference(TIMEZONE_STORAGE_KEY, 'america_chicago'));
  const [clockFormat, setClockFormat] = useState<ClockFormat>(() =>
    readStoredPreference(CLOCK_STORAGE_KEY, '12h')
  );
  const [language, setLanguage] = useState<string>(() => readStoredPreference(LANGUAGE_STORAGE_KEY, 'en'));
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>(() =>
    readStoredPreference(TEMPERATURE_STORAGE_KEY, 'fahrenheit')
  );
  const [cameraNotifications, setCameraNotifications] = useState(CAMERA_NOTIFICATIONS);
  const [hvacNotifications, setHvacNotifications] = useState(HVAC_NOTIFICATIONS);

  const currentContext = user?.contexts?.find((c) => c.context === selectedContext);
  const companyName = currentContext?.contextDisplayName ?? t('profile.empty', '—');
  const roleLabel = currentContext?.roleName ?? activeRole ?? t('profile.empty', '—');

  const makePreferenceHandler =
    <T extends string>(setter: (value: T) => void, key: string) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as T;
      setter(value);
      localStorage.setItem(key, value);
    };

  const handleTimeZoneChange = makePreferenceHandler(setTimeZone, TIMEZONE_STORAGE_KEY);
  const handleClockChange = makePreferenceHandler<ClockFormat>(setClockFormat, CLOCK_STORAGE_KEY);
  const handleLanguageChange = makePreferenceHandler(setLanguage, LANGUAGE_STORAGE_KEY);
  const handleTemperatureChange = makePreferenceHandler<TemperatureUnit>(setTemperatureUnit, TEMPERATURE_STORAGE_KEY);

  const toggleCameraNotification = (id: string, field: 'email' | 'push' | 'inApp') => {
    setCameraNotifications((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: !row[field] } : row)));
  };

  const toggleHvacNotification = (id: string, field: 'email' | 'push' | 'inApp') => {
    setHvacNotifications((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: !row[field] } : row)));
  };

  const manageResideoIdUrl = import.meta.env.VITE_RESIDEO_ID_URL ?? '#';

  return (
    <div className={styles.container}>
      <div className={styles.titleBar}>
        <ButtonIcon
          size="small"
          iconType="icon"
          aria-label={t('profile.back', 'Back')}
          onClick={() => navigate(-1)}
        >
          <img src={chevronLeftIcon} width="20" height="20" alt="" aria-hidden="true" />
        </ButtonIcon>
        <h1 className={styles.title}>{t('profile.title', 'My Profile')}</h1>
      </div>
      <div className={styles.body}>
        <nav className={styles.nav} aria-label={t('profile.navLabel', 'Profile navigation')}>
          <Button
            variant="text"
            className={`${styles.navItem} ${activeTab === 'profile' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile.tabs.profile', 'Profile')}
          </Button>
          <Button
            variant="text"
            className={`${styles.navItem} ${activeTab === 'preferences' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            {t('profile.tabs.preferences', 'Preferences')}
          </Button>
          <Button
            variant="text"
            className={`${styles.navItem} ${activeTab === 'notifications' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            {t('profile.tabs.notifications', 'Notifications')}
          </Button>
        </nav>

        <div className={styles.content}>
          {activeTab === 'profile' && (
            <>
              <h2 className={styles.name}>{user?.name ?? t('profile.empty', '—')}</h2>
              <hr className={styles.divider} />
              <div className={styles.profileCard}>
              <h3 className={styles.profileCardTitle}>{t('profile.information', 'Profile Information')}</h3>
              <div className={styles.infoGrid}>
                <div>
                  <h3 className={styles.colHeader}>{t('profile.company', 'Company')}</h3>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.companyLabel', 'Company')}</span>
                    <span className={styles.fieldValue}>{companyName}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.branch', 'Branch')}</span>
                    <span className={styles.fieldValue}>{t('profile.empty', '—')}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.role', 'Role')}</span>
                    <span className={styles.fieldValue}>{roleLabel}</span>
                  </div>
                </div>
                <div>
                  <h3 className={styles.colHeader}>{t('profile.contact', 'Contact')}</h3>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.email', 'Email')}</span>
                    <span className={styles.fieldValue}>{user?.email ?? t('profile.empty', '—')}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.phone', 'Phone')}</span>
                    <span className={styles.fieldValue}>{t('profile.empty', '—')}</span>
                  </div>
                </div>
              </div>
              </div>

              <div className={styles.notice}>
                <p className={styles.noticeText}>
                  {t(
                    'profile.notice',
                    'Your name, email, and password can be updated inside your Resideo ID settings.'
                  )}
                </p>
                <ButtonLink className={styles.noticeLink} href={manageResideoIdUrl} target="_blank" rel="noopener noreferrer">
                  {t('profile.manageResideoId', 'Manage Resideo ID')}
                  <img src={externalLinkIcon} width="14" height="14" alt="" aria-hidden="true" />
                </ButtonLink>
              </div>
            </>
          )}

          {activeTab === 'preferences' && (
            <>
              <h3 className={styles.sectionLabel}>{t('profile.tabs.preferences', 'Preferences')}</h3>

              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.timeZone', 'Time Zone')}</p>
                  <p className={styles.prefDesc}>
                    {t('profile.timeZoneDesc', 'Select the timezone where you are located')}
                  </p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="timeZone"
                    value={timeZone}
                    onChange={handleTimeZoneChange}
                    items={[
                        { value: 'america_chicago', label: t('profile.timezones.america_chicago', 'Central Time (US & Canada)') },
                        { value: 'america_new_york', label: t('profile.timezones.america_new_york', 'Eastern Time (US & Canada)') },
                        { value: 'america_denver', label: t('profile.timezones.america_denver', 'Mountain Time (US & Canada)') },
                        { value: 'america_los_angeles', label: t('profile.timezones.america_los_angeles', 'Pacific Time (US & Canada)') },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.clockFormat', 'Clock Format')}</p>
                  <p className={styles.prefDesc}>
                    {t('profile.clockFormatDesc', 'Select your preferred clock formatting')}
                  </p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="clockFormat"
                    value={clockFormat}
                    onChange={handleClockChange}
                    items={[
                      { value: '12h', label: t('profile.12Hour', '12 Hour') },
                      { value: '24h', label: t('profile.24Hour', '24 Hour') },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.language', 'Language')}</p>
                  <p className={styles.prefDesc}>{t('profile.languageDesc', 'Select the language you want to utilize')}</p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="language"
                    value={language}
                    onChange={handleLanguageChange}
                    items={[
                        { value: 'en', label: t('profile.languages.en', 'English') },
                        { value: 'es', label: t('profile.languages.es', 'Español') },
                        { value: 'fr', label: t('profile.languages.fr', 'Français') },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.temperatureUnit', 'Temperature Unit')}</p>
                  <p className={styles.prefDesc}>
                    {t('profile.temperatureUnitDesc', 'Select which temperature unit to display')}
                  </p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="temperatureUnit"
                    value={temperatureUnit}
                    onChange={handleTemperatureChange}
                    items={[
                      { value: 'fahrenheit', label: t('profile.fahrenheit', 'Fahrenheit (°F)') },
                      { value: 'celsius', label: t('profile.celsius', 'Celsius (°C)') },
                    ]}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <div className={styles.notificationsPane} aria-label={t('profile.notificationSettings', 'Notification Settings')}>
              <h3 className={styles.sectionLabel}>{t('profile.notificationSettings', 'Notification Settings')}</h3>
              <NotificationTable
                title={t('profile.cameras', 'Cameras')}
                devicesLabel={t('profile.allCameras', 'All Cameras')}
                rows={cameraNotifications}
                onToggle={toggleCameraNotification}
              />
              <NotificationTable
                title={t('profile.hvac', 'HVAC')}
                devicesLabel={t('profile.allThermostats', 'All Thermostats')}
                rows={hvacNotifications}
                onToggle={toggleHvacNotification}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
